package org.teddapp.project.sync

import com.tedd.db.TeddDatabase

/**
 * Les prêts et leurs remboursements dans le moteur de synchronisation. Les soldes ne
 * voyagent pas avec eux : les transactions liées ont leur propre table, et le compte
 * son solde, chacun synchronisé de son côté.
 */
internal object LoanSyncOps {

    fun dirtyQuery(entity: EntityKind, db: TeddDatabase): app.cash.sqldelight.Query<*> = when (entity) {
        EntityKind.Loan -> db.loanQueries.selectDirty()
        else -> db.loanPaymentQueries.selectDirty()
    }

    fun selectDirty(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Loan -> db.loanQueries.selectDirty().executeAsList().map { loan(it) }
        else -> db.loanPaymentQueries.selectDirty().executeAsList().map { payment(it) }
    }

    fun selectAll(entity: EntityKind, db: TeddDatabase): List<RemoteRow> = when (entity) {
        EntityKind.Loan -> db.loanQueries.selectAll().executeAsList().map { loan(it) }
        else -> db.loanPaymentQueries.selectAll().executeAsList().map { payment(it) }
    }

    fun markSynced(entity: EntityKind, db: TeddDatabase, id: String, updatedAt: Long, syncedAt: Long) {
        when (entity) {
            EntityKind.Loan -> db.loanQueries.markSynced(syncedAt, id, updatedAt)
            else -> db.loanPaymentQueries.markSynced(syncedAt, id, updatedAt)
        }
    }

    fun selectByIdAnyState(entity: EntityKind, db: TeddDatabase, id: String): LocalSnapshot? = when (entity) {
        EntityKind.Loan -> db.loanQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
        else -> db.loanPaymentQueries.selectByIdAnyState(id).executeAsOneOrNull()
            ?.let { LocalSnapshot(it.updated_at, it.is_deleted == 1L) }
    }

    fun insertFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Loan -> db.loanQueries.insert(
                row.id,
                p.str("direction") ?: "lent",
                p.str("counterparty") ?: "",
                p.long("principal") ?: 0L,
                p.long("interest") ?: 0L,
                p.str("currency") ?: "XOF",
                p.str("date") ?: "",
                p.str("due_date"),
                p.str("note"),
                p.str("account_id"),
                p.str("tx_id"),
                p.str("status") ?: "open",
                p.long("order_index") ?: 0L,
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
            else -> db.loanPaymentQueries.insert(
                row.id,
                p.str("loan_id") ?: "",
                p.long("amount") ?: 0L,
                p.str("date") ?: "",
                p.str("account_id") ?: "",
                p.str("tx_id"),
                p.str("note"),
                p.long("created_at") ?: row.updatedAt,
                row.updatedAt,
            )
        }
    }

    fun updateFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        val p = row.payload
        when (entity) {
            EntityKind.Loan -> db.loanQueries.updateFromRemote(
                p.str("direction") ?: "lent",
                p.str("counterparty") ?: "",
                p.long("principal") ?: 0L,
                p.long("interest") ?: 0L,
                p.str("currency") ?: "XOF",
                p.str("date") ?: "",
                p.str("due_date"),
                p.str("note"),
                p.str("account_id"),
                p.str("tx_id"),
                p.str("status") ?: "open",
                p.long("order_index") ?: 0L,
                row.updatedAt,
                row.id,
            )
            else -> db.loanPaymentQueries.updateFromRemote(
                p.str("loan_id") ?: "",
                p.long("amount") ?: 0L,
                p.str("date") ?: "",
                p.str("account_id") ?: "",
                p.str("tx_id"),
                p.str("note"),
                row.updatedAt,
                row.id,
            )
        }
    }

    fun softDeleteFromRemote(entity: EntityKind, db: TeddDatabase, row: RemoteRow, deletedAt: Long) {
        when (entity) {
            EntityKind.Loan -> db.loanQueries.softDelete(deletedAt, row.updatedAt, row.id)
            else -> db.loanPaymentQueries.softDelete(deletedAt, row.updatedAt, row.id)
        }
    }

    fun restoreIfDeleted(entity: EntityKind, db: TeddDatabase, row: RemoteRow) {
        when (entity) {
            EntityKind.Loan -> db.loanQueries.restore(row.updatedAt, row.id)
            else -> db.loanPaymentQueries.restore(row.updatedAt, row.id)
        }
    }

    private fun loan(it: com.tedd.db.Loan) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "direction" to it.direction,
        "counterparty" to it.counterparty,
        "principal" to it.principal,
        "interest" to it.interest,
        "currency" to it.currency,
        "date" to it.date,
        "due_date" to it.due_date,
        "note" to it.note,
        "account_id" to it.account_id,
        "tx_id" to it.tx_id,
        "status" to it.status,
        "order_index" to it.order_index,
        "created_at" to it.created_at,
    ))

    private fun payment(it: com.tedd.db.Loan_payment) = RemoteRow(it.id, it.updated_at, it.is_deleted == 1L, it.deleted_at, mapOf(
        "loan_id" to it.loan_id,
        "amount" to it.amount,
        "date" to it.date,
        "account_id" to it.account_id,
        "tx_id" to it.tx_id,
        "note" to it.note,
        "created_at" to it.created_at,
    ))
}
